import { format, parseISO } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../../../containers/ApiContainer";
import { PlacementService } from "../../../services/PlacementService";
import { TripWithTenant } from "../../../types";
import { isInfinityDate } from "../../../utils/dateHelpers";
import { ScheduleListPresenter } from "./ScheduleListPresenter";

export interface ScheduleListProps {
  trips: TripWithTenant[];
  onTripEdit?: (trip: TripWithTenant) => void;
  loading?: boolean;
  startDate?: Date;
  endDate?: Date;
  onTripsUpdated?: () => void;
  statusFilter?: string;
  tailNumberFilter?: string;
}

export const ScheduleList: React.FC<ScheduleListProps> = ({
  trips,
  onTripEdit,
  loading = false,
  startDate,
  endDate,
  onTripsUpdated,
  statusFilter: externalStatusFilter,
  tailNumberFilter: externalTailNumberFilter,
}) => {
  const { postgrest } = useApi();
  const placementService = useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );
  // Use external filters if provided, otherwise manage locally (for backward compatibility)
  const [localStatusFilter, setLocalStatusFilter] = useState<string>("all");
  const [localTailNumberFilter, setLocalTailNumberFilter] = useState<string>("");

  const statusFilter = externalStatusFilter !== undefined ? externalStatusFilter : localStatusFilter;
  const tailNumberFilter = externalTailNumberFilter !== undefined ? externalTailNumberFilter : localTailNumberFilter;
  const setStatusFilter = externalStatusFilter !== undefined ? () => {} : setLocalStatusFilter;
  const setTailNumberFilter = externalTailNumberFilter !== undefined ? () => {} : setLocalTailNumberFilter;
  const [tripPlacements, setTripPlacements] = useState<Record<string, string>>(
    {}
  );

  // Filter trips
  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      const matchesStatus =
        statusFilter === "all" || trip.status === statusFilter;
      const matchesTailNumber =
        !tailNumberFilter ||
        (trip.tail_number &&
          trip.tail_number
            .toLowerCase()
            .includes(tailNumberFilter.toLowerCase()));

      return matchesStatus && matchesTailNumber;
    });
  }, [trips, statusFilter, tailNumberFilter]);

  // Format date for display
  const formatDate = (dateString: string) => {
    if (isInfinityDate(dateString)) {
      return "No scheduled departure";
    }
    return format(parseISO(dateString), "MMM d, yyyy h:mm a");
  };

  // Get status chip color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "primary";
      case "active":
        return "success";
      case "completed":
        return "default";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  // Load placement data for all trips
  useEffect(() => {
    const loadTripPlacements = async () => {
      if (!filteredTrips.length) return;

      const placementPromises = filteredTrips.map(async (trip) => {
        try {
          const placements = await placementService.getPlacementsWithLocation(
            trip.trip_id
          );
          const locationNames = placements
            .map((p) => p.location_name)
            .filter((name) => name && name !== "Unassigned");

          return {
            tripId: trip.trip_id,
            assignment:
              locationNames.length > 0
                ? locationNames.join(", ")
                : "Unassigned",
          };
        } catch (error) {
          return { tripId: trip.trip_id, assignment: "Unassigned" };
        }
      });

      const results = await Promise.all(placementPromises);
      const placementMap = results.reduce((acc, { tripId, assignment }) => {
        acc[tripId] = assignment;
        return acc;
      }, {} as Record<string, string>);

      setTripPlacements(placementMap);
    };

    loadTripPlacements();
  }, [filteredTrips, placementService]);

  // Get trip type (determine if trip is for base tenant or transient)
  const getTripType = (trip: TripWithTenant): string => {
    // Base tenants have infinite departure dates
    return isInfinityDate(trip.departure_date) ? "Base Tenant" : "Transient";
  };

  // Get trip location assignments from cached placement data
  const getTripAssignments = (trip: TripWithTenant): string => {
    return tripPlacements[trip.trip_id] || "Loading...";
  };

  return (
    <ScheduleListPresenter
      filteredTrips={filteredTrips}
      trips={trips}
      tripPlacements={tripPlacements}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      tailNumberFilter={tailNumberFilter}
      setTailNumberFilter={setTailNumberFilter}
      showFilters={externalStatusFilter === undefined && externalTailNumberFilter === undefined}
      loading={loading}
      startDate={startDate}
      endDate={endDate}
      onTripEdit={onTripEdit}
      formatDate={formatDate}
      getStatusColor={getStatusColor}
      getTripType={getTripType}
      getTripAssignments={getTripAssignments}
    />
  );
};